'use client';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import MenuContainer from './MenuContainer';
import { IKButton } from '@/components/shared/material';
import { useMatchMedia } from '@/lib/matchMedia';
import { m, useScroll } from 'framer-motion';
import LazyMotionWrapper from '@/components/shared/LazyMotionWrapper';
import { MobileMenu } from './MobileMenu';
import { MenuItemLink } from '@/types/types';
import IKLink from '@/components/shared/material/IKLink';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  cartItemsQuantityAtom,
  isCartSlideOutOpenAtom,
} from '@/store/cartStore';
import { ShoppingBagOpen } from '@phosphor-icons/react';
import { navbarBgColorAtom, navbarOverlayAtom } from '@/store/globalStore';
import { ContactModal } from '@/components/shared/modals';
import { PortableTextBlock } from 'sanity';
import { siteIsNavigatingAtom } from '@/store/globalStore';
import { usePathname, useSearchParams } from 'next/navigation';
import { testIds } from '@/constants/testIds';

export const Navbar = (props: {
  menuItems: any[] | null;
  loginLink?: MenuItemLink;
  signUpLink?: MenuItemLink;
  overlay?: boolean;
  simpleMode?: boolean;
  contactModal?: {
    heading: PortableTextBlock;
    textContentTop: PortableTextBlock;
    textContentBottom: PortableTextBlock;
    buttons: {
      whatsAppButton: {
        text: string;
        link: MenuItemLink;
      };
      chatbotButton: {
        text: string;
      };
      callbackButton: {
        text: string;
        link: MenuItemLink;
      };
    };
  };
}) => {
  const menuItems = props.menuItems ?? [];
  const loginLink = props.loginLink ?? undefined;
  const signUpLink = props.signUpLink ?? undefined;
  const overlay = props.overlay ?? undefined;
  const simpleMode = props.simpleMode ?? false;
  const navbarBgColor = useAtomValue(navbarBgColorAtom);
  const navbarOverlay = useAtomValue(navbarOverlayAtom);
  const [siteIsNavigating, setSiteIsNavigating] = useAtom(siteIsNavigatingAtom);
  const pathname = usePathname();
  //we use showNavAnimation to flash the white circular background you see when the navigation animates
  const [showNavAnimation, setShowNavAnimation] = useState<boolean | null>(
    false
  );
  const searchParams = useSearchParams();

  useEffect(() => {
    setSiteIsNavigating(false);
  }, [pathname]);

  useEffect(() => {
    if (siteIsNavigating) {
      setShowNavAnimation(true);
      setTimeout(() => {
        setShowNavAnimation(null);
      }, 500);
    }
  }, [siteIsNavigating]);

  // Use to show the cart items quantity, show Sign Up when zero
  const cartItemsQuantity = useAtomValue(cartItemsQuantityAtom);
  const setOpenCartSlideOut = useSetAtom(isCartSlideOutOpenAtom);

  // To determine which desktop nav option is selected (Desktop)
  const [selectedNavOption, setSelectedNavOption] = useState<number | null>(
    null
  );
  // To determine the position of the selected nav option (Desktop)
  const [selectedNavOptionPosition, setSelectedNavOptionPosition] = useState<{
    x: number;
  }>({ x: 0 });

  // To determine if the user has scrolled, sets the background of the sticky nav
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);

  // Get scroll progress from framer-motion
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    scrollYProgress.onChange((latest) => {
      // If the user has scrolled more than 0.5% of the page, set hasScrolled to true
      setHasScrolled(latest > 0.005);
    });
  }, [scrollYProgress]);

  // To determine if the nav is open (Mobile)
  const [isNavOpen, setIsNavOpen] = useState<boolean>(false);

  // To determine if the contact modal is open
  const [isContactModalOpen, setContactModalOpen] = useState(false);

  const openModal = useCallback(() => {
    setIsNavOpen(false);
    setContactModalOpen(true);
  }, []);

  // Open contact modal if the query param is present
  useEffect(() => {
    const isModalOpen = searchParams.get('contactModal') === 'open';

    if (isModalOpen) {
      setContactModalOpen(true);
    } else {
      setContactModalOpen(false);
    }
  }, [searchParams]);

  // To store refs for each menu item (MenuContainer)
  const menuItemRefs = useRef(
    menuItems.map(() => React.createRef<HTMLParagraphElement>())
  );

  // To store refs for each nav option (this component)
  const navItemRefs = useRef(
    menuItems.map(() => React.createRef<HTMLButtonElement>())
  );

  // To determine if the user is on desktop resolution
  const isDesktopResolution = useMatchMedia('(min-width:1080px)', true);

  /**
   * useEffect hook that handles resizing of the window.
   * Resets the selectedNavOption and selectedNavOptionPosition
   * if they are not null or their x-coordinate is not 0.
   *
   * @returns A cleanup function to remove the event listener.
   */
  useEffect(() => {
    const resizeHandler = () => {
      if (selectedNavOption !== null || selectedNavOptionPosition.x !== 0) {
        setSelectedNavOptionPosition({ x: 0 });
        setSelectedNavOption(null);
      }
    };
    document.addEventListener('resize', resizeHandler);

    return () => document.removeEventListener('resize', resizeHandler);
  }, [selectedNavOption, selectedNavOptionPosition.x]);

  /**
   * Handles the hover event on a navigation option.
   *
   * @param navItemId - The ID of the navigation option being hovered.
   */
  const onNavOptionHover = (navItemId: number) => {
    if (selectedNavOption === navItemId) return;

    const navOptionRef = navItemRefs.current[navItemId];
    if (!navOptionRef || !navOptionRef.current) return;

    const navOptionScreenPosition =
      navOptionRef.current.getBoundingClientRect();

    setSelectedNavOptionPosition({
      x: navOptionScreenPosition.left + navOptionScreenPosition.width / 2,
    });

    setSelectedNavOption(navItemId);
  };

  /**
   * Handles the mouse leave event on the navbar.
   */
  const onMouseLeave = () => {
    setSelectedNavOption(null);
    setSelectedNavOptionPosition({ x: 0 });
  };

  /**
   * useEffect hook that handles hiding of the chatbot as well as body
   * scrolling when the nav is open or closed.
   */
  useEffect(() => {
    // Hide chat bot and stop body from scrolling on mobile resolution
    if (isNavOpen && !isDesktopResolution) {
      // Stop body from scrolling
      document.body.style.overflow = 'hidden';
      // Hide chat bot
      const webChatPublic = document.querySelector('.cx-webChat-public');
      if (webChatPublic) {
        const webChatPublic = document.querySelector(
          '.cx-webChat-public'
        ) as HTMLElement;
        webChatPublic.style.display = 'none';
      }
    } else {
      document.body.style.overflow = '';

      // Show chat bot
      const webChatPublic = document.querySelector('.cx-webChat-public');
      if (webChatPublic) {
        const webChatPublic = document.querySelector(
          '.cx-webChat-public'
        ) as HTMLElement;
        webChatPublic.style.display = 'block';
      }
    }

    // Close nav on desktop resolution
    if (isDesktopResolution) {
      setIsNavOpen(false);
    }
  }, [isNavOpen, isDesktopResolution]);

  const onNavOptionClicked = (navItemId: number) => {
    selectedNavOption !== null ? onMouseLeave() : onNavOptionHover(navItemId);
  };

  const isOverlay = () => {
    if (overlay !== undefined) {
      return overlay;
    }
    if (navbarOverlay === 'overlay') {
      return true;
    }

    return false;
  };

  return (
    <LazyMotionWrapper>
      <m.header
        initial={false}
        animate={isNavOpen ? 'open' : 'closed'}
        className={`!z-700 flex w-full items-center justify-center transition-all duration-200 sm:!z-500 ${
          isOverlay()
            ? 'fixed'
            : '!sticky top-0 bg-yellow-150 bg-gradient-to-r !from-yellow-400 to-yellow-200'
        } ${
          hasScrolled
            ? 'h-[70px] bg-yellow-200 bg-gradient-to-r !from-yellow-400 !to-yellow-300'
            : `h-[70px] bg-transparent md:h-[80px]`
        }`}
      >
        <div
          onMouseLeave={onMouseLeave}
          className={`relative top-0 h-full w-full transition-all duration-300 ${
            !hasScrolled && isOverlay() ? 'md:pt-5' : ''
          }`}
        >
          <nav
            className={`container-padding relative z-50 h-full w-full items-center justify-center ${
              isNavOpen && !isDesktopResolution
                ? 'backdrop-blur-sm transition-colors delay-500 duration-100'
                : 'bg-transparent'
            }`}
          >
            <div className='flex h-full w-full items-center justify-between'>
              <IKLink
                link={{ url: '/', type: 'internal', document: 'home' }}
                className='relatve z-20 flex items-center justify-center'
              >
                <div
                  className={`absolute h-[400px] w-[400px] scale-0 select-none rounded-full bg-white opacity-0 ${showNavAnimation ? 'navigating-animation' : ''}`}
                ></div>
                {siteIsNavigating ? (
                  <>
                    <m.svg
                      width='81'
                      height='85'
                      viewBox='0 0 81 85'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      animate={{
                        scale: [1.5, 1.7, 1.5],
                      }}
                      transition={{
                        scale: {
                          repeat: Infinity,
                          duration: 2,
                        },
                      }}
                      className={`relative z-10 h-10 w-10 md:h-10 md:w-10 lg:h-11 lg:w-11 xl:h-12 xl:w-12 ${hasScrolled ? 'text-charcoal' : `${navbarBgColor === 'charcoal' ? 'text-charcoal' : 'text-white'}`}`}
                    >
                      <m.path
                        d='M44.4196 12.1429L21.1983 15.7854C17.0181 16.3372 13.7703 19.3355 13.7703 23.6725V62.1774C13.7703 66.5145 16.7723 69.3852 21.1983 70.0645L59.8022 74.8C61.7704 74.7935 63.6563 73.9606 65.0481 72.4829C66.4399 71.0052 67.2244 69.0026 67.2303 66.9128V18.088'
                        stroke='white'
                        strokeWidth='10'
                        strokeLinecap='round'
                        fill='none'
                        animate={{
                          pathLength: [0, 1, 0],
                          pathOffset: [0, 1],
                        }}
                        transition={{
                          duration: 1,
                          ease: 'linear',
                          repeat: Infinity,
                        }}
                      />
                      <path
                        d='M58.632 13.6L22.3678 18.0197C18.4409 18.5348 15.3899 21.3331 15.3899 25.381V61.3189C15.3899 65.3669 18.21 68.0462 22.3678 68.6803L58.632 73.1C60.4809 73.094 62.2525 72.3166 63.56 70.9374C64.8674 69.5581 65.6044 67.6891 65.6099 65.7387V20.9612C65.6044 19.0107 64.8674 17.1418 63.56 15.7626C62.2525 14.3833 60.4809 13.6059 58.632 13.6ZM24.4053 26.5133C24.7675 26.1487 25.1965 25.866 25.666 25.6824C26.1354 25.4989 26.6355 25.4185 27.1354 25.4461C27.6379 25.4311 28.1381 25.5239 28.6055 25.7189C29.073 25.9138 29.4981 26.2069 29.8551 26.5803C30.2084 26.956 30.4846 27.4045 30.6666 27.8978C30.8485 28.391 30.9323 28.9185 30.9126 29.4474C30.9337 30.5586 30.5696 31.6397 29.8885 32.4875C29.5663 32.9088 29.1559 33.2456 28.6895 33.4714C28.2232 33.6972 27.7136 33.806 27.2007 33.789C26.6688 33.7881 26.1439 33.6609 25.6648 33.417C25.1858 33.173 24.765 32.8184 24.4335 32.3797C23.7348 31.5517 23.349 30.4816 23.3495 29.373C23.3225 28.8446 23.4026 28.3161 23.5847 27.8229C23.7668 27.3298 24.0465 26.8833 24.4053 26.5133ZM30.479 59.1547C30.2544 59.3664 29.9886 59.5238 29.7003 59.616C29.412 59.7082 29.1081 59.7328 28.8099 59.6882C27.4381 59.7884 26.0625 59.563 24.7842 59.0282C24.0792 58.3793 24.0563 57.8047 24.0263 56.1239C23.9312 50.9567 24.0351 44.2239 23.9223 38.9934C23.8501 38.5949 23.8599 38.1845 23.9512 37.7902C24.0424 37.396 24.2129 37.027 24.4511 36.7083C24.6542 36.4869 24.8987 36.3123 25.169 36.1955C25.4392 36.0787 25.7295 36.0222 26.0215 36.0297C26.0215 36.0297 29.2628 35.9571 30.0595 36.9389C30.8562 37.9207 30.8086 38.3928 30.8474 40.0719C30.9654 45.2409 30.8844 51.89 30.9672 57.0609C30.9936 58.3699 30.9831 58.541 30.479 59.1547ZM58.3957 61.5123C57.6051 62.1697 56.6381 62.5477 55.6322 62.5927C54.7413 62.6389 53.8637 62.3518 53.154 61.7818C52.1303 60.7392 51.2111 59.5877 50.4116 58.3458C48.4728 55.5196 44.9917 51.5163 42.778 48.4577C42.8238 51.3005 42.6845 55.1514 42.7162 57.775C42.7674 58.5982 42.5372 59.4134 42.0676 60.0731C41.6658 60.5697 40.7088 60.5714 40.2875 60.6309C40.0108 60.6569 36.9617 61.0381 36.1243 59.9245C35.1778 59.0245 35.3736 57.9404 35.3736 56.6408C35.021 48.0523 35.2573 37.1061 35.2096 28.3783C35.2096 26.8778 35.3772 26.5189 35.8354 25.9723C36.2897 25.57 36.8715 25.364 37.4639 25.3958C40.1782 25.3958 40.8374 25.5651 41.6465 26.5617C42.5189 27.6364 42.5277 28.3132 42.577 30.2562C42.6351 32.7218 42.6193 36.9964 42.5136 39.7874C45.6738 35.3696 49.1372 30.1539 51.7669 25.6264C52.6076 24.178 53.5294 24.1538 53.799 24.1575C54.9843 24.0693 56.1576 24.4542 57.0863 25.2359C57.4407 25.537 57.7282 25.9166 57.9283 26.3478C58.1283 26.7789 58.2362 27.2512 58.2443 27.7312C58.3388 28.502 58.2354 29.2855 57.9446 29.9996C57.3915 31.2753 56.7196 32.49 55.9388 33.6255C54.2292 36.3457 51.6348 40.8231 49.6236 43.826C50.7958 45.5924 52.7733 48.5357 53.7498 49.9861C54.7262 51.4365 54.4989 51.0645 55.352 52.3046C56.2049 53.5449 57.6345 55.5791 58.5351 56.8118C58.8331 57.1945 59.086 57.6141 59.2876 58.0613C59.4319 58.3918 59.499 58.7539 59.4833 59.1174C59.4057 60.0278 59.0197 60.8781 58.3957 61.5123Z'
                        fill='black'
                      />
                    </m.svg>
                  </>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='31'
                    height='35'
                    viewBox='0 0 31 35'
                    className={`h-10 w-10 md:h-10 md:w-10 lg:h-11 lg:w-11 xl:h-12 xl:w-12 ${hasScrolled ? 'text-charcoal' : `${navbarBgColor === 'charcoal' ? 'text-charcoal' : 'text-white'}`}`}
                    fill='none'
                  >
                    <path
                      d='M26.2541 0.852051L4.65589 3.34965C2.31709 3.6407 0.5 5.22206 0.5 7.50951V27.8181C0.5 30.1056 2.17957 31.6197 4.65589 31.978L26.2541 34.4756C27.3553 34.4722 28.4104 34.0329 29.1891 33.2535C29.9678 32.4741 30.4067 31.4179 30.41 30.3157V5.01191C30.4067 3.90967 29.9678 2.85352 29.1891 2.07412C28.4104 1.29471 27.3553 0.855377 26.2541 0.852051ZM5.86938 8.1494C6.0851 7.94335 6.3406 7.78357 6.62021 7.67986C6.89981 7.57615 7.19763 7.5307 7.49541 7.54628C7.79468 7.53783 8.09254 7.59028 8.37096 7.70045C8.64938 7.81062 8.90255 7.97623 9.11515 8.18723C9.32559 8.39957 9.49012 8.65301 9.59848 8.93175C9.70683 9.2105 9.75671 9.50859 9.74499 9.80746C9.75758 10.4354 9.5407 11.0463 9.13509 11.5254C8.94318 11.7635 8.69875 11.9538 8.42099 12.0814C8.14324 12.209 7.83972 12.2705 7.53425 12.2609C7.21746 12.2604 6.90484 12.1885 6.61955 12.0507C6.33427 11.9128 6.08362 11.7124 5.88617 11.4645C5.47004 10.9966 5.24027 10.3919 5.24059 9.76543C5.22448 9.46679 5.27223 9.16815 5.38067 8.88946C5.4891 8.61078 5.65572 8.35847 5.86938 8.1494ZM9.48675 26.5951C9.35297 26.7147 9.1947 26.8037 9.023 26.8558C8.85129 26.9079 8.6703 26.9218 8.49266 26.8966C7.67565 26.9532 6.85636 26.8258 6.09507 26.5236C5.67518 26.1569 5.66153 25.8322 5.64368 24.8824C5.587 21.9624 5.64893 18.1577 5.58175 15.2019C5.53873 14.9767 5.54458 14.7448 5.59892 14.522C5.65325 14.2992 5.7548 14.0907 5.89667 13.9106C6.01766 13.7855 6.16324 13.6868 6.32421 13.6208C6.48518 13.5548 6.65807 13.5229 6.83198 13.5271C6.83198 13.5271 8.76244 13.4861 9.23692 14.0409C9.71139 14.5957 9.68305 14.8625 9.70615 15.8114C9.77648 18.7324 9.72819 22.4898 9.77753 25.4119C9.79327 26.1516 9.78697 26.2483 9.48675 26.5951ZM26.1134 27.9274C25.6425 28.2989 25.0666 28.5125 24.4675 28.5379C23.9369 28.564 23.4142 28.4018 22.9915 28.0797C22.3818 27.4905 21.8344 26.8398 21.3582 26.138C20.2035 24.5409 18.1302 22.2786 16.8118 20.5502C16.8391 22.1567 16.7561 24.3328 16.775 25.8154C16.8055 26.2806 16.6684 26.7413 16.3887 27.1141C16.1494 27.3947 15.5794 27.3957 15.3285 27.4293C15.1637 27.444 13.3477 27.6594 12.849 27.0301C12.2853 26.5215 12.4019 25.9089 12.4019 25.1745C12.1919 20.3211 12.3326 14.1354 12.3042 9.20329C12.3042 8.35535 12.404 8.15256 12.6769 7.84364C12.9475 7.61631 13.294 7.49994 13.6468 7.51791C15.2634 7.51791 15.656 7.61353 16.1379 8.17672C16.6575 8.78405 16.6627 9.16652 16.6921 10.2645C16.7267 11.6578 16.7173 14.0734 16.6543 15.6506C18.5365 13.1541 20.5992 10.2067 22.1654 7.6482C22.6661 6.82968 23.2151 6.81602 23.3757 6.81812C24.0816 6.76827 24.7804 6.9858 25.3335 7.42755C25.5446 7.5977 25.7158 7.81218 25.835 8.05584C25.9541 8.2995 26.0184 8.56639 26.0232 8.83764C26.0795 9.27322 26.0179 9.71596 25.8447 10.1195C25.5153 10.8404 25.1151 11.5268 24.6501 12.1685C23.6319 13.7057 22.0867 16.2359 20.8889 17.9328C21.587 18.931 22.7648 20.5943 23.3464 21.4139C23.9279 22.2335 23.7925 22.0233 24.3006 22.7241C24.8086 23.425 25.66 24.5745 26.1964 25.2711C26.3739 25.4874 26.5245 25.7245 26.6446 25.9772C26.7305 26.164 26.7705 26.3686 26.7611 26.574C26.7149 27.0885 26.485 27.569 26.1134 27.9274Z'
                      fill='currentColor'
                    />
                  </svg>
                )}
              </IKLink>

              {isDesktopResolution && (
                <>
                  <div
                    data-test-qa={testIds.siteHeader}
                    className='z-20 hidden items-center justify-center gap-10 [@media(min-width:1080px)]:flex'
                  >
                    {!simpleMode &&
                      menuItems.map((item, index) => (
                        <Fragment key={index}>
                          {item.type === 'dropdown' && (
                            <button
                              ref={navItemRefs.current[index]}
                              className={`text-base font-medium ${hasScrolled ? 'text-charcoal hover:text-charcoal/50' : `${navbarBgColor === 'charcoal' ? 'text-charcoal hover:text-charcoal/50' : 'text-white hover:text-white/50'}`} ${
                                selectedNavOption === index
                                  ? `${navbarBgColor === 'charcoal' ? 'text-charcoal/50' : 'text-white/50'}`
                                  : ''
                              }`}
                              onMouseEnter={() => onNavOptionHover(index)}
                              onClick={() => onNavOptionClicked(index)}
                              onFocus={() => onNavOptionHover(index)}
                              onTouchStart={() => onNavOptionClicked(index)}
                            >
                              <span>{item.text}</span>
                            </button>
                          )}
                          {item.type === 'contact-us' && (
                            <IKButton
                              variant='text'
                              size='sm'
                              className={`text-base font-medium ${hasScrolled ? 'text-charcoal hover:text-charcoal/50' : `${navbarBgColor === 'charcoal' ? 'text-charcoal hover:text-charcoal/50' : 'text-white hover:text-white/50'}`} ${
                                selectedNavOption === index
                                  ? `${navbarBgColor === 'charcoal' ? 'text-charcoal/50' : 'text-white/50'}`
                                  : ''
                              }`}
                              onMouseEnter={() => onMouseLeave()}
                              onClick={() => openModal()}
                            >
                              <span>{item.text}</span>
                            </IKButton>
                          )}
                          {item.type !== 'dropdown' &&
                            item.type !== 'contact-us' && (
                              <IKLink
                                onMouseEnter={() => onMouseLeave()}
                                link={item.link}
                                className={`text-base font-medium ${hasScrolled ? 'text-charcoal hover:!text-charcoal/50' : `${navbarBgColor === 'charcoal' ? 'text-charcoal hover:!text-charcoal/50' : 'text-white hover:!text-white/50'}`}`}
                              >
                                <span>{item.text}</span>
                              </IKLink>
                            )}
                        </Fragment>
                      ))}
                  </div>
                  <div
                    data-test-qa={testIds.siteSignupAndLogin}
                    className='z-20 hidden gap-2 [@media(min-width:1080px)]:flex'
                  >
                    {!simpleMode && (
                      <IKButton
                        size='sm'
                        variant='text'
                        color='charcoal'
                        link={loginLink}
                        className={`px-5 text-base font-medium !delay-0 !duration-200 ${hasScrolled ? 'text-charcoal hover:!text-charcoal/50' : `${navbarBgColor === 'charcoal' ? 'text-charcoal hover:!text-charcoal/50' : 'text-white hover:!text-white/50'}`}`}
                      >
                        Login
                      </IKButton>
                    )}

                    {cartItemsQuantity === 0 && !simpleMode && (
                      <IKButton
                        size='sm'
                        variant='filled'
                        color={`${navbarBgColor === 'white' ? 'yellow' : 'charcoal'}`}
                        className='!py-2.5 px-5 text-base'
                        link={signUpLink}
                      >
                        <span>Sign Up</span>
                      </IKButton>
                    )}
                    {cartItemsQuantity > 0 && (
                      <IKButton
                        size='sm'
                        variant='filled'
                        color='white'
                        className='!py-2.5 px-4 text-base shadow-none'
                        onClick={() => {
                          setOpenCartSlideOut(true);
                        }}
                        data-test-qa={testIds.purchaseJourneyConfirmPurchase}
                      >
                        <span className='flex gap-2'>
                          <ShoppingBagOpen weight='fill' className='h-8 w-8' />
                          <div className='flex-1 select-none items-center rounded-full bg-yellow px-3 py-1 text-base font-bold'>
                            {cartItemsQuantity}
                          </div>
                        </span>
                      </IKButton>
                    )}
                  </div>
                </>
              )}
              {!isDesktopResolution && (
                // Menu Toggle
                <div className='z-20 flex gap-4'>
                  {cartItemsQuantity > 0 && (
                    <IKButton
                      size='sm'
                      variant='filled'
                      color='white'
                      className='!py-2 px-4 text-base shadow-none'
                      onClick={() => {
                        setOpenCartSlideOut(true);
                      }}
                      data-test-qa={testIds.purchaseJourneyCartSlideOutButton}
                    >
                      <span className='flex gap-2'>
                        <ShoppingBagOpen weight='fill' className='h-8 w-8' />
                        <div className='flex-1 select-none items-center rounded-full bg-yellow px-3 py-1 text-base font-bold'>
                          {cartItemsQuantity}
                        </div>
                      </span>
                    </IKButton>
                  )}

                  {/* phone */}

                  {!simpleMode && (
                    <IKButton
                      size='sm'
                      aria-label='Contact Us'
                      variant='text'
                      className={` ${
                        !isNavOpen
                          ? 'bg-[#ECB008] !p-1'
                          : 'border border-gray-500 bg-white !p-0'
                      } flex h-12 w-12 items-center justify-center !rounded-full transition-all duration-300`}
                      onClick={() => openModal()}
                    >
                      <div
                        className={` ${
                          !isNavOpen
                            ? 'bg-gradient-to-r from-[#FFCF41] to-[#FFE283]'
                            : 'bg-white'
                        } flex h-full w-full items-center justify-center rounded-full transition-all duration-300 active:bg-gradient-to-r active:from-[#ffcf414d] active:to-[#ffe2834d]`}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='21'
                          height='21'
                          viewBox='0 0 21 21'
                          fill='none'
                        >
                          <path
                            d='M20.4882 15.6563C20.3099 17.0113 19.6444 18.2551 18.6161 19.1554C17.5878 20.0556 16.2669 20.5508 14.9001 20.5483C6.96007 20.5483 0.500009 14.0883 0.500009 6.14819C0.497559 4.78147 0.992701 3.46059 1.89296 2.43225C2.79321 1.40391 4.03701 0.738446 5.39206 0.560138C5.73471 0.518298 6.08171 0.5884 6.38125 0.759978C6.68079 0.931556 6.91681 1.19541 7.05407 1.51215L9.16609 6.22719V6.23919C9.27118 6.48165 9.31458 6.74636 9.29242 7.00968C9.27026 7.273 9.18323 7.52673 9.03909 7.74821C9.02109 7.77521 9.00209 7.80021 8.98209 7.82521L6.90007 10.2932C7.64908 11.8152 9.24109 13.3933 10.7831 14.1443L13.2171 12.0732C13.241 12.0532 13.2661 12.0345 13.2921 12.0172C13.5134 11.8697 13.768 11.7796 14.0329 11.7551C14.2978 11.7307 14.5646 11.7726 14.8091 11.8772L14.8221 11.8832L19.5332 13.9943C19.8505 14.131 20.115 14.3669 20.2871 14.6664C20.4593 14.966 20.5298 15.3133 20.4882 15.6563ZM13.3001 8.54822H17.3002C17.5123 8.54822 17.7158 8.46393 17.8659 8.3139C18.0159 8.16387 18.1002 7.96038 18.1002 7.74821C18.1002 7.53603 18.0159 7.33255 17.8659 7.18252C17.7158 7.03249 17.5123 6.9482 17.3002 6.9482H15.2322L18.6662 3.51417C18.8163 3.36405 18.9006 3.16045 18.9006 2.94816C18.9006 2.73587 18.8163 2.53227 18.6662 2.38216C18.5161 2.23204 18.3125 2.14771 18.1002 2.14771C17.8879 2.14771 17.6843 2.23204 17.5342 2.38216L14.1001 5.81719V3.74817C14.1001 3.53599 14.0159 3.33251 13.8658 3.18248C13.7158 3.03245 13.5123 2.94816 13.3001 2.94816C13.088 2.94816 12.8845 3.03245 12.7344 3.18248C12.5844 3.33251 12.5001 3.53599 12.5001 3.74817V7.74821C12.5001 7.96038 12.5844 8.16387 12.7344 8.3139C12.8845 8.46393 13.088 8.54822 13.3001 8.54822Z'
                            fill='black'
                          />
                        </svg>
                      </div>
                    </IKButton>
                  )}
                  {!simpleMode && (
                    <button
                      onClick={() => setIsNavOpen(!isNavOpen)}
                      className={` ${
                        !isNavOpen && navbarBgColor === 'charcoal'
                          ? 'bg-[#282825]'
                          : ''
                      } ${!isNavOpen ? 'shadow-lg' : ''} -mr-6 flex h-12 w-14 flex-col items-center justify-center gap-1.5 rounded-l-xl px-3 py-3 sm:-mr-8 lg:-mr-10`}
                    >
                      <span
                        className={`${
                          isNavOpen ? 'translate-y-1 rotate-45' : ''
                        } h-[3px] w-full transform rounded-lg ${
                          isNavOpen && navbarBgColor === 'charcoal'
                            ? 'bg-charcoal'
                            : hasScrolled
                              ? 'bg-yellow-200'
                              : `bg-yellow`
                        } transition-transform duration-150`}
                      ></span>
                      {!isNavOpen && (
                        <span
                          className={`h-[3px] w-full transform rounded-lg ${
                            isNavOpen && navbarBgColor === 'charcoal'
                              ? 'bg-charcoal'
                              : hasScrolled
                                ? 'bg-yellow-200'
                                : `bg-yellow`
                          } transition-transform duration-150`}
                        ></span>
                      )}
                      <span
                        className={`${
                          isNavOpen ? '-translate-y-1 rotate-[-45deg]' : ''
                        } h-[3px] w-full transform rounded-lg ${
                          isNavOpen && navbarBgColor === 'charcoal'
                            ? 'bg-charcoal'
                            : hasScrolled
                              ? 'bg-yellow-200'
                              : `bg-yellow`
                        } transition-transform duration-150`}
                      ></span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </nav>
          {isDesktopResolution && !simpleMode && (
            <MenuContainer
              refs={menuItemRefs}
              hasScrolled={hasScrolled}
              menuItems={menuItems}
              onNavItemClick={onMouseLeave}
              selectedNavOption={selectedNavOption}
              selectedNavOptionPosition={selectedNavOptionPosition}
            />
          )}

          {/* Mobile Menu  */}
          {!isDesktopResolution && !simpleMode && (
            <MobileMenu
              menuItems={menuItems}
              loginLink={loginLink}
              signUpLink={signUpLink}
              isNavOpen={isNavOpen}
              closeNavbar={() => setIsNavOpen(false)}
            />
          )}
        </div>
      </m.header>
      {/* Modal */}
      <ContactModal
        open={isContactModalOpen}
        handler={setContactModalOpen}
        modal={props.contactModal}
      />
    </LazyMotionWrapper>
  );
};
